/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBalanceScale,
  FaCalendar,
  FaChartBar,
  FaChevronDown,
  FaChevronRight,
  FaExclamationTriangle,
  FaFolderOpen,
  FaGavel,
  FaHistory,
  FaHome,
  FaList,
  FaPlus,
  FaShieldAlt,
  FaSignOutAlt,
  FaTasks,
  FaUserShield,
  FaSearch, // Added for Crime Search (Director only)
  FaUsers, // Added for External Police
  FaUserFriends, // Added for Member/Police
  FaBuilding, // Added for Director
  FaClipboardList, // Added for Screening Coordinator
  FaUserTie, // Added for Deputy/General Attorney
} from "react-icons/fa";
import { Link, useLocation } from "react-router-dom";
import { Roles } from "types";
import { hasRole } from "utils/helper";
import logo from "./assets/logo.png";

type IconComp = React.ComponentType<{ className?: string }>;
const Icons: Record<string, IconComp> = {
  BalanceScale: FaBalanceScale as unknown as IconComp,
  Calendar: FaCalendar as unknown as IconComp,
  ChartBar: FaChartBar as unknown as IconComp,
  ChevronDown: FaChevronDown as unknown as IconComp,
  ChevronRight: FaChevronRight as unknown as IconComp,
  ExclamationTriangle: FaExclamationTriangle as unknown as IconComp,
  FolderOpen: FaFolderOpen as unknown as IconComp,
  Gavel: FaGavel as unknown as IconComp,
  History: FaHistory as unknown as IconComp,
  Home: FaHome as unknown as IconComp,
  List: FaList as unknown as IconComp,
  Plus: FaPlus as unknown as IconComp,
  ShieldAlt: FaShieldAlt as unknown as IconComp,
  SignOutAlt: FaSignOutAlt as unknown as IconComp,
  Tasks: FaTasks as unknown as IconComp,
  UserShield: FaUserShield as unknown as IconComp,
  Search: FaSearch as unknown as IconComp,
  Users: FaUsers as unknown as IconComp,
  UserFriends: FaUserFriends as unknown as IconComp,
  Building: FaBuilding as unknown as IconComp,
  ClipboardList: FaClipboardList as unknown as IconComp,
  UserTie: FaUserTie as unknown as IconComp,
};

// Define MenuItem type for better type safety
type MenuItem = {
  label: string;
  route?: string;
  active?: string[];
  icon?: React.ReactNode;
  subMenu?: MenuItem[];
};

const Sidebar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { loginData } = useAuth();
  const [isAppealOpen, setIsAppealOpen] = useState(false);
  const [isCassationOpen, setIsCassationOpen] = useState(false);
  const [openAssetMenu, setOpenAssetMenu] = useState<string | null>(null);

  // Cassation routes contain "cassation" (/cassation-appeal, /defendant-cassation).
  // Appeal routes contain "appeal" but must exclude the cassation ones.
  const isCassationActive = location.pathname.includes("cassation");
  const isAppealActive =
    !isCassationActive && location.pathname.includes("appeal");

  useEffect(() => {
    if (isAppealActive) {
      setIsAppealOpen(true);
    }
  }, [isAppealActive]);

  useEffect(() => {
    if (isCassationActive) {
      setIsCassationOpen(true);
    }
  }, [isCassationActive]);

  const navItem = (
    title: string,
    path: string,
    activeArr?: string[],
    icon?: React.ReactNode,
  ) => {
    const isActive =
      location.pathname === path ||
      (activeArr?.some((pattern) => {
        if (!pattern) return false;
        const p = pattern.startsWith("/") ? pattern : `/${pattern}`;
        if (p === "/") return location.pathname === "/";
        if (p.includes("/:")) {
          const base = p.split("/:")[0];
          return location.pathname.startsWith(`${base}/`);
        }
        return location.pathname === p || location.pathname.startsWith(`${p}/`);
      }) ??
        false);

    return (
      <Link to={path}>
        <div
          className={`flex items-center px-3 py-2 rounded font-medium transition-colors ${
            isActive
              ? "bg-white text-black"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          {icon && <span className="mr-3 text-sm">{icon}</span>}
          {title}
        </div>
      </Link>
    );
  };

  const buildAppealPath = (filter: string) => `/${filter}`;

  const appealDropdownItem = (
    title: string,
    path: string,
    icon?: React.ReactNode,
  ) => {
    const pathOnly = path.split("?")[0];
    // Active for the list page and its detail pages (e.g. /prosecutor-appeal/:id).
    const isActive =
      location.pathname === pathOnly ||
      location.pathname.startsWith(`${pathOnly}/`);

    return (
      <Link to={path}>
        <div
          className={`flex items-center pl-8 pr-3 py-2 text-sm rounded font-medium transition-colors ${
            isActive
              ? "bg-gray-700 text-white"
              : "hover:bg-gray-800 text-gray-300"
          }`}
        >
          {icon && <span className="mr-3 text-xs">{icon}</span>}
          {title}
        </div>
      </Link>
    );
  };

  const AppealDropdown = () => (
    <div className="mb-1">
      <button
        onClick={() => setIsAppealOpen(!isAppealOpen)}
        className={`flex items-center justify-between w-full px-3 py-2 rounded font-medium transition-colors ${
          isAppealActive
            ? "bg-white text-black"
            : "hover:bg-gray-800 hover:text-white"
        }`}
      >
        <div className="flex items-center">
          <Icons.Gavel className="mr-3 text-sm" />
          {t("appeal")}
        </div>
        <span className="text-xs">
          {isAppealOpen ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
        </span>
      </button>

      {isAppealOpen && (
        <div className="mt-1 ml-2 space-y-1">
          {appealDropdownItem(
            t("prosecutor"),
            buildAppealPath("prosecutor-appeal"),
            <Icons.Gavel />,
          )}
          {/* {appealDropdownItem(
            "Higher Admin",
            buildAppealPath("higher-admin"),
            <Icons.UserShield />,
            "higher-admin",
          )} */}
          {appealDropdownItem(
            t("defendant"),
            buildAppealPath("defendant-appeal"),
            <Icons.BalanceScale />,
          )}
        </div>
      )}
    </div>
  );

  const CassationDropdown = () => (
    <div className="mb-1">
      <button
        onClick={() => setIsCassationOpen(!isCassationOpen)}
        className={`flex items-center justify-between w-full px-3 py-2 rounded font-medium transition-colors ${
          isCassationActive
            ? "bg-white text-black"
            : "hover:bg-gray-800 hover:text-white"
        }`}
      >
        <div className="flex items-center">
          <Icons.BalanceScale className="mr-3 text-sm" />
          {t("cassation")}
        </div>
        <span className="text-xs">
          {isCassationOpen ? <Icons.ChevronDown /> : <Icons.ChevronRight />}
        </span>
      </button>

      {isCassationOpen && (
        <div className="mt-1 ml-2 space-y-1">
          {appealDropdownItem(
            t("prosecutor"),
            buildAppealPath("cassation-appeal"),
            <Icons.Gavel />,
          )}
          {appealDropdownItem(
            t("defendant"),
            buildAppealPath("defendant-cassation"),
            <Icons.BalanceScale />,
          )}
        </div>
      )}
    </div>
  );

  // Menu configurations with icons
  const incidentMenu: MenuItem = {
    label: t("incident"),
    route: "/incident-list",
    active: ["incident-list", "incident/:id", "incident-register"],
    icon: <Icons.ExclamationTriangle />,
  };

  const prosecutorMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("upcoming_hearings"),
      route: "/upcoming-hearings",
      active: ["upcoming-hearings"],
      icon: <Icons.Calendar />,
    },
    {
      label: t("appeal"),
      route: "/appeal",
      active: ["appeal", "appeal/:id", "appeal/create", "appeal/history"],
      icon: <Icons.Gavel />,
    },
    {
      label: t("bail_right"),
      route: "/bail-right",
      active: ["bail-right", "bail-right/:id", "bail-right-request"],
      icon: <Icons.BalanceScale />,
    },
    {
      label: t("sidebar_witness_protection"),
      route: "/witness-protection",
      active: [
        "witness-protection",
        "witness-protection/:id",
        "witness-protection-request",
      ],
      icon: <Icons.ShieldAlt />,
    },
  ];

  const appealMenu: MenuItem[] = [
    {
      label: t("appeal"),
      route: "/appeal",
      active: ["appeal", "appeal/:id", "appeal/create", "appeal/history"],
      icon: <Icons.Gavel />,
    },
  ];

  const coordinatorMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("upcoming_hearings"),
      route: "/upcoming-hearings",
      active: ["upcoming-hearings"],
      icon: <Icons.Calendar />,
    },
    {
      label: t("appeal"),
      route: "/appeal",
      active: ["appeal", "appeal/:id", "appeal/create", "appeal/history"],
      icon: <Icons.Gavel />,
    },
    {
      label: t("bail_right"),
      route: "/bail-right",
      active: ["bail-right", "bail-right/:id", "bail-right-request"],
      icon: <Icons.BalanceScale />,
    },
    {
      label: t("sidebar_witness_protection"),
      route: "/witness-protection",
      active: [
        "witness-protection",
        "witness-protection/:id",
        "witness-protection-request",
      ],
      icon: <Icons.ShieldAlt />,
    },
    {
      label: t("sidebar_schedule"),
      route: "/schedules",
      active: ["schedules"],
      icon: <Icons.Calendar />,
    },
  ];

  const registrarMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("upcoming_hearings"),
      route: "/upcoming-hearings",
      active: ["upcoming-hearings"],
      icon: <Icons.Calendar />,
    },
    {
      label: t("appeal"),
      route: "/appeal",
      active: ["appeal", "appeal/:id"],
      icon: <Icons.Gavel />,
    },
    {
      label: t("asset_recovery"),
      route: "/asset-recovery",
      active: ["/asset-recovery", "/asset-recovery/", "/asset-register"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("asset_appeal"),
      subMenu: [
        {
          label: t("prosecutor_appeal"),
          route: "/asset-appeal",
          active: ["/asset-appeal", "/asset-appeal/"],
          icon: <Icons.Gavel />,
        },
        {
          label: t("respondent_appeal"),
          route: "/asset-appeal-respondent",
          active: ["/asset-appeal-respondent", "/asset-appeal-respondent/"],
          icon: <Icons.BalanceScale />,
        },
      ],
    },
    {
      label: t("bail_right"),
      route: "/bail-right",
      active: ["bail-right", "bail-right/:id", "bail-right-request"],
      icon: <Icons.BalanceScale />,
    },
    {
      label: t("sidebar_witness_protection"),
      route: "/witness-protection",
      active: [
        "witness-protection",
        "witness-protection/:id",
        "witness-protection-request",
      ],
      icon: <Icons.ShieldAlt />,
    },
    {
      label: t("sidebar_schedule"),
      route: "/schedules",
      active: ["schedules"],
      icon: <Icons.Calendar />,
    },
  ];

  const directorMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("sidebar_witness_protection"),
      route: "/witness-protection",
      active: [
        "witness-protection",
        "witness-protection/:id",
        "witness-protection-request",
      ],
      icon: <Icons.ShieldAlt />,
    },
  ];

  const clerkMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("further_investigation"),
      route: "/further-investigation",
      active: ["further-investigation", "/further-investigation/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
  ];

  const screeningCoordinatorMenus: MenuItem[] = [
    {
      label: t("incident"),
      route: "/incident-list",
      active: ["incident-list", "incident/:id", "incident-registter"],
      icon: <Icons.ExclamationTriangle />,
    },
  ];

  const policeMenus: MenuItem[] = [
    {
      label: t("economic_corruption_investigation"),
      route: "/economic-coruption-investigation",
      active: ["economic-coruption-investigation"],
      icon: <Icons.ChartBar />,
    },
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("further_investigation"),
      route: "/further-investigation",
      active: ["further-investigation", "/further-investigation/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("sidebar_schedule"),
      route: "/schedules",
      active: ["schedules"],
      icon: <Icons.Calendar />,
    },
  ];

  const deputyMenus: MenuItem[] = [
    {
      label: t("investigation_file"),
      route: "/investigation-files",
      active: ["investigation-files", "/investigation-files/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("charge"),
      route: "/charges",
      active: ["charges", "/charges/:id"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("bail_right"),
      route: "/bail-right",
      active: ["bail-right", "bail-right/:id", "bail-right-request"],
      icon: <Icons.BalanceScale />,
    },
    {
      label: t("sidebar_witness_protection"),
      route: "/witness-protection",
      active: [
        "witness-protection",
        "witness-protection/:id",
        "witness-protection-request",
      ],
      icon: <Icons.ShieldAlt />,
    },
    {
      label: t("sidebar_schedule"),
      route: "/schedules",
      active: ["schedules"],
      icon: <Icons.Calendar />,
    },
  ];

  // NEW: Asset Prosecutor Menus
  const assetProsecutorMenus: MenuItem[] = [
    {
      label: t("asset_recovery"),
      route: "/asset-recovery",
      active: ["/asset-recovery", "/asset-recovery/", "/asset-register"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("asset_appeal"),
      subMenu: [
        {
          label: t("prosecutor_appeal"),
          route: "/asset-appeal",
          active: ["/asset-appeal", "/asset-appeal/"],
          icon: <Icons.Gavel />,
        },
        {
          label: t("respondent_appeal"),
          route: "/asset-appeal-respondent",
          active: ["/asset-appeal-respondent", "/asset-appeal-respondent/"],
          icon: <Icons.UserShield />,
        },
      ],
    },
  ];

  // NEW: Asset Coordinator Menus
  const assetCoordinatorMenus: MenuItem[] = [
    {
      label: t("asset_recovery"),
      route: "/asset-recovery",
      active: ["/asset-recovery", "/asset-recovery/", "/asset-register"],
      icon: <Icons.FolderOpen />,
    },
    {
      label: t("asset_appeal"),
      icon: <Icons.FolderOpen />,
      subMenu: [
        {
          label: t("prosecutor_appeal"),
          route: "/asset-appeal",
          active: ["/asset-appeal", "/asset-appeal/"],
          icon: <Icons.Gavel />,
        },
        {
          label: t("respondent_appeal"),
          route: "/asset-appeal-respondent",
          active: ["/asset-appeal-respondent", "/asset-appeal-respondent/"],
          icon: <Icons.UserShield />,
        },
      ],
    },
  ];

  const incidentByMobileMenu: MenuItem[] = [
    {
      label: t("incident_by_mobile"),
      route: "/incident-by-mobile",
      active: ["incident-by-mobile", "incident-by-mobile/:id"],
      icon: <Icons.ExclamationTriangle />,
    },
  ];

  // Helper component for menus with submenus
  const MenuWithSubMenu = ({ menu }: { menu: MenuItem }) => {
    const isActive = menu.subMenu?.some(
      (subItem) =>
        location.pathname === subItem.route ||
        subItem.active?.some(
          (pattern) =>
            location.pathname.startsWith(pattern.replace("/:", "/")) ||
            location.pathname === pattern,
        ),
    );

    return (
      <div className="mb-1">
        <button
          onClick={() =>
            setOpenAssetMenu(openAssetMenu === menu.label ? null : menu.label)
          }
          className={`flex items-center justify-between w-full px-3 py-2 rounded font-medium transition-colors ${
            isActive
              ? "bg-white text-black"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          <div className="flex items-center">
            {menu.icon && <span className="mr-3 text-sm">{menu.icon}</span>}
            {menu.label}
          </div>
          <span className="text-xs">
            {openAssetMenu === menu.label ? (
              <Icons.ChevronDown />
            ) : (
              <Icons.ChevronRight />
            )}
          </span>
        </button>

        {openAssetMenu === menu.label && menu.subMenu && (
          <div className="mt-1 ml-2 space-y-1">
            {menu.subMenu.map((subItem, index) =>
              navItem(
                subItem.label,
                subItem.route || "#",
                subItem.active,
                subItem.icon,
              ),
            )}
          </div>
        )}
      </div>
    );
  };

  let selectedMenus: MenuItem[] = [];

  // CorruptionAppealCoordinator can only view the appeal tab on the sidebar.
  const isCorruptionAppealCoordinator = hasRole(loginData?.roles, [
    Roles.corruptionAppealCoordinator,
  ]);

  if (isCorruptionAppealCoordinator) {
    selectedMenus = appealMenu;
  } else if (
    hasRole(loginData?.roles, [
      // NOTE: the economic/corruption registrar names are swapped in types.ts,
      // so match both string variants to reliably catch CaseCorruptionRegistrar.
      Roles.economicRegistrar, // "CaseCorruptionRegistrar"
      Roles.corruptionRegistrar, // "CaseEconomicRegistrar"
    ])
  ) {
    // Corruption registrar: incident tab plus the standard registrar menus.
    selectedMenus = [incidentMenu, ...registrarMenus];
  } else if (hasRole(loginData?.roles, [Roles.registrar])) {
    selectedMenus = registrarMenus;
  } else if (
    hasRole(loginData?.roles, [
      Roles.appealProsecutor,
      loginData?.structure?.admin_unit?.parent_unit
        ? Roles.childAppealCoordinator
        : "",
    ])
  ) {
    selectedMenus = appealMenu;
  } else if (hasRole(loginData?.roles, [Roles.economicProsecutor])) {
    selectedMenus = [incidentMenu, ...prosecutorMenus];
  } else if (hasRole(loginData?.roles, [Roles.prosecutor])) {
    selectedMenus = prosecutorMenus;
  } else if (
    hasRole(loginData?.roles, [
      Roles.coordinator,
      Roles.variousCoordinator,
      Roles.economicCoordinator,
      Roles.corruptionCoordinator,
      Roles.corruptionDirector,
    ])
  ) {
    selectedMenus = hasRole(loginData?.roles, [
      Roles.economicCoordinator,
      Roles.corruptionDirector,
    ])
      ? [incidentMenu, ...coordinatorMenus]
      : coordinatorMenus;
  } else if (hasRole(loginData?.roles, [Roles.registrar])) {
    selectedMenus = registrarMenus;
  } else if (hasRole(loginData?.roles, [Roles.economicDirector])) {
    selectedMenus = coordinatorMenus;
  } else if (
    hasRole(loginData?.roles, [
      Roles.prosecutionDirector,
      Roles.economicDirector,
    ])
  ) {
    selectedMenus = coordinatorMenus;
  } else if (hasRole(loginData?.roles, [Roles.economicClerk])) {
    selectedMenus = [incidentMenu, ...clerkMenus];
  } else if (hasRole(loginData?.roles, [Roles.clerk, Roles.corruptionClerk])) {
    selectedMenus = clerkMenus;
  } else if (hasRole(loginData?.roles, [Roles.screeningTeamCoordinator])) {
    selectedMenus = screeningCoordinatorMenus;
  } else if (hasRole(loginData?.roles, [Roles.member], loginData)) {
    selectedMenus = policeMenus;
  } else if (hasRole(loginData?.roles, [Roles.deputy])) {
    selectedMenus = deputyMenus;
  } else if (hasRole(loginData?.roles, [Roles.generalAttorney])) {
    selectedMenus = deputyMenus;
  } else if (loginData?.role === "Member" || loginData?.is_police) {
    selectedMenus = policeMenus;
  } else if (
    hasRole(loginData?.roles, [
      Roles.appealCorruptionProsecutor,
      Roles.appealEconomicProsecutor,
      Roles.appealVariousProsecutor,
      Roles.variousAppealCoordinator,
      Roles.economicAppealCoordinator,
      Roles.corruptionAppealCoordinator,
      Roles.appealChildAndWomenCaseProsecutor,
    ])
  ) {
    selectedMenus = appealMenu;
  }

  if (
    hasRole(loginData?.roles, [Roles.assetCoordinator]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = assetCoordinatorMenus;
  }

  if (
    hasRole(loginData?.roles, [Roles.assetProsecutor]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = assetProsecutorMenus;
  }

  // Corruption director also gets access to the asset recovery & asset appeal tabs.
  if (
    hasRole(loginData?.roles, [Roles.corruptionDirector]) &&
    !isCorruptionAppealCoordinator
  ) {
    selectedMenus = [
      ...selectedMenus,
      {
        label: t("asset_recovery"),
        route: "/asset-recovery",
        active: ["/asset-recovery", "/asset-recovery/", "/asset-register"],
        icon: <Icons.FolderOpen />,
      },
      {
        label: t("asset_appeal"),
        icon: <Icons.FolderOpen />,
        subMenu: [
          {
            label: t("prosecutor_appeal"),
            route: "/asset-appeal",
            active: ["/asset-appeal", "/asset-appeal/"],
            icon: <Icons.Gavel />,
          },
          {
            label: t("respondent_appeal"),
            route: "/asset-appeal-respondent",
            active: ["/asset-appeal-respondent", "/asset-appeal-respondent/"],
            icon: <Icons.UserShield />,
          },
        ],
      },
    ];
  }

  return (
    <aside className="flex-col justify-between hidden w-64 min-h-screen text-white bg-black md:flex">
      <div className="p-4">
        {loginData?.is_police !== true && (
          <div className="flex items-center gap-2 mb-4">
            <img src={logo} alt="Logo" className="w-12 h-10 rounded" />
            <div className="text-xs leading-tight">
              <div className="text-sm font-semibold">
                Biiroo Abba Alangaa W.O.{" "}
              </div>
              <div className="text-gray-400 text-[11px]">
                የኦሮሚያ ጠቅላይ ዐቃቤ ሕግ ቢሮ
              </div>
            </div>
          </div>
        )}

        <nav className="flex flex-col space-y-1 text-sm">
          {loginData && (
            <Section title={t("pd_dashboard")}>
              {navItem(t("pd_dashboard"), "/", ["/"], <Icons.Home />)}
            </Section>
          )}
          {/* {loginData &&
            loginData?.role !== "Member" &&
            loginData?.is_police !== true && (
              <Section title="DASHBOARD">
                {navItem("Dashboard", "/", ["/"], <Icons.Home />)}
                {navItem(
                  "Reports",
                  "/reports",
                  ["reports"],
                  <Icons.ChartBar />,
                )}
              </Section>
            )} */}

          <Section title={t("crime_case")}>
            {selectedMenus.map((menu, index) => {
              if (menu.label === t("appeal")) {
                return (
                  <React.Fragment key={index}>
                    <AppealDropdown />
                    <CassationDropdown />
                  </React.Fragment>
                );
              } else if (menu.subMenu) {
                return <MenuWithSubMenu key={index} menu={menu} />;
              } else {
                return (
                  <div key={index}>
                    {navItem(
                      menu.label,
                      menu.route || "#",
                      menu?.active,
                      menu.icon,
                    )}
                  </div>
                );
              }
            })}
          </Section>
          {hasRole(loginData?.roles, [Roles.prosecutionDirector]) && (
            <Section title={t("crime_search")}>
              {navItem(
                t("crime_search"),
                "/crime-search",
                ["crime-search"],
                <Icons.Search />,
              )}
            </Section>
          )}
          {hasRole(loginData?.roles, [
            Roles.economicProsecutor,
            Roles.economicCoordinator,
            Roles.economicClerk,
            Roles.economicAppealCoordinator,
          ]) && (
            <Section title={t("incident_by_mobile")}>
              {incidentByMobileMenu.map((menu, index) => (
                <div key={index}>
                  {navItem(
                    menu.label,
                    menu.route || "#",
                    menu?.active,
                    menu.icon,
                  )}
                </div>
              ))}
            </Section>
          )}
        </nav>
      </div>
      <div className="p-4 text-xs text-gray-400">{t("powered_by_eaii")}</div>
    </aside>
  );
};

export default Sidebar;

// Section component
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4">
      <div className="mb-2 text-xs font-semibold text-gray-400 uppercase">
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
